import { FAIcon, Theme, SearchDataStructure, AttributeTypes } from '../../../ui/components';
import { GetColumnsType } from '../../../ui/components/TableContainer/types/propsTypes';
import { DynamicObjectInterface } from '../../../sdk/type';
import { dateTimeToCustomFormat, formatDateDMYYYY } from '../../../ui/utils';

export interface LeaderData {
  recId: number;
  aboId: string;
  campaignId: string;
  campaignName: string;
  campaignStart?: string;
  campaignEnd?: string;
  email: string;
  enrolledDate: string;
  loginId: number;
  name: string;
  phone: string;
  pin: string;
  totalCoach: number;
  campaignStatRecId?: string;
  pointRemark?: string;
  totalQualified?: number;
  totalQualifiedCoach: {
    value: string;
    valueAdj: string;
    lastUpdateDate?: string;
  };
  totalPoint?: {
    value: string;
    valueAdj: string;
  };
  totalAboReqruitment?: {
    value: string;
    valueAdj: string;
    lastUpdateDate?: string;
  };
  newGroupAboCount?: {
    value: string;
    valueAdj: string;
    lastUpdateDate?: string;
  };
  remarks?: string;
  rewards: RewardData[];
  coaches?: CoachData[];
  summary?: {
    qualifiedCount: number;
    completedCount: number;
    achievedCount: number;
    alpCount: number;
    newAboCount: number;
  };
  //isSelfQualifiedCoach?: number;
}

export interface RewardData {
  rewardRemarks?: string;
  rewardId?: number;
  rewardCode?: string;
  rewardName?: string;
  rewardType?: string;
  redeemDate?: string;
  rewardRecId?: string;
  rewardStatus?: string;
  isNew?: boolean;
}

export interface CoachData {
  recId?: number;
  coachId?: string;
  coachName?: string;
  coachEnrollmentDate?: string;
  coachEndDate?: string;
  coachTotalTeams?: number;
  completedCount?: number;
  achievedCount?: number;
  ALPCount?: number;
  newAbo?: number;
  qualified?: number;
  //points?: number;
  //qualifiedCoach?: number;
  //groupNewAbo?: number;
}

export interface MasterData {
  campaign: {
    campaignEnd?: string;
    campaignId?: string;
    campaignName?: string;
    campaignStart: string;
    recId?: number;
    status?: string;
  }[];
  reward?: {
    recId: number;
    rewardCode: string;
    rewardName: string;
  }[];
}

export interface CampaignOption {
  campaignEnd: string;
  campaignId: string;
  campaignName: string;
  campaignStart: string;
  status: string;
  title: string;
  value: string;
}

export const dataSearchOption: SearchDataStructure[] = [
  {
    name: 'Leader Name',
    key: 'name',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
  {
    name: 'Leader ABO ID',
    key: 'aboId',
    type: AttributeTypes.Textbox,
    visibility: true,
  },
];

export const getLeaderListConfig: GetColumnsType = (
  styles: Object,
  translation: Function,
  cb: DynamicObjectInterface,
) => [
  {
    key: 'name',
    text: translation('admin.generalLeader.leaderName'),
    width: 6,
    sortable: true,
    render: (object: LeaderData) => <span>{object.name}</span>,
  },
  {
    key: 'aboId',
    text: translation('admin.generalLeader.leaderAboId'),
    sortable: true,
    width: 3,
    render: (object: LeaderData) => <span>{object.aboId}</span>,
  },
  {
    key: 'pin',
    text: translation('admin.generalLeader.leaderPin'),
    sortable: true,
    width: 3,
    render: (object: LeaderData) => <span>{object.pin}</span>,
  },
  {
    key: 'totalCoach',
    text: translation('admin.generalLeader.totalCoach'),
    sortable: true,
    width: 2,
    render: (object: LeaderData) => <span>{object.totalCoach || 0}</span>,
  },
  {
    key: 'totalQualifiedCoach',
    text: translation('admin.generalLeader.totalQualifiedCoach'),
    sortable: false,
    width: 3,
    render: (object: LeaderData) => (
      <span>{(object.totalQualifiedCoach.value ?? 0) + (object.totalQualifiedCoach.valueAdj ?? 0)}</span>
    ),
  },
  {
    key: 'action-func',
    width: 1,
    text: 'Action',
    render: (object: LeaderData) => (
      <FAIcon
        iconName={'edit'}
        onClick={e => {
          cb?.onEdit(object);
          e.stopPropagation();
        }}
        theme={Theme.PURPLE}
        title={'Edit'}
      />
    ),
  },
];

export const getRewardListConfig: GetColumnsType = (
  styles: Object,
  translation: Function,
  cb: DynamicObjectInterface,
) => [
  {
    key: 'rewardName',
    text: translation('admin.rewardMaster.rewardName'),
    sortable: true,
    width: 5,
    render: (object: RewardData) => <span>{object.rewardName}</span>,
  },
  {
    key: 'redeemDate',
    text: translation('admin.participant.collectDate'),
    sortable: true,
    width: 5,
    render: (object: RewardData) => (
      <span>{object.redeemDate ? formatDateDMYYYY(new Date(object.redeemDate)) : null}</span>
    ),
  },
];

export const getCoachListConfig: GetColumnsType = (
  styles: Object,
  translation: Function,
  cb: DynamicObjectInterface,
) => [
  {
    key: 'coachId',
    text: translation('admin.generalCoach.coachId'),
    width: 3,
    sortable: true,
    render: (object: CoachData) => <span>{object.coachId}</span>,
  },
  {
    key: 'coachName',
    text: translation('admin.generalCoach.coachName'),
    sortable: true,
    width: 5,
    render: (object: CoachData) => <span>{object.coachName}</span>,
  },
  {
    key: 'declareStartMonth',
    text: translation('admin.generalLeader.declareStartMonth'),
    sortable: true,
    width: 3,
    render: (object: CoachData) => (
      <span>
        {object.coachEnrollmentDate ? dateTimeToCustomFormat(object.coachEnrollmentDate?.toString(), 'MMM YYYY') : ''}
      </span>
    ),
  },
  {
    key: 'totalTeams',
    text: translation('admin.generalLeader.totalTeams'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object.coachTotalTeams}</span>,
  },
  {
    key: 'completedCount',
    text: translation('admin.generalCoach.completedCount'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object?.completedCount ?? 0}</span>,
  },
  {
    key: 'achievedCount',
    text: translation('admin.generalCoach.achievedCount'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object?.achievedCount ?? 0}</span>,
  },
  {
    key: 'ALPCount',
    text: translation('admin.generalCoach.ALPCount'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object?.ALPCount ?? 0}</span>,
  },
  {
    key: 'sponsoredNewAbos',
    text: translation('admin.generalLeader.sponsoredNewAbos'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object.newAbo ?? 0}</span>,
  },
  {
    key: 'qualifiedCoach',
    text: translation('admin.generalLeader.qualifiedCoach'),
    sortable: true,
    width: 2,
    render: (object: CoachData) => <span>{object.qualified ? 'YES' : 'NO'}</span>,
  },
  {
    key: 'action-func',
    width: 1,
    text: 'Action',
    render: (object: CoachData) => (
      <FAIcon
        iconName={'edit'}
        onClick={e => {
          cb?.onEdit(object);
          e.stopPropagation();
        }}
        theme={Theme.PURPLE}
        title={'Edit'}
      />
    ),
  },
];
